import { useEffect, useState } from 'react';

import type { KubernetesConsoleApi } from './api';
import type { ApiResource } from './types';

export interface ApiResourcesState {
  resources: ApiResource[];
  loading: boolean;
  error: string | null;
}

/**
 * The resource catalogue for a cluster: every group/version/kind it serves,
 * CRDs included. This is what lets the console open a resource nobody wrote a
 * page for — an operator's CRD, an ASO type, a kro composition.
 *
 * Failure is not fatal. The curated kinds still work; only the "all
 * resources" picker goes empty, so this never blocks the page.
 */
export function useApiResources(
  api: KubernetesConsoleApi,
  cluster: string,
): ApiResourcesState {
  const [state, setState] = useState<ApiResourcesState>({
    resources: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!cluster) {
      setState({ resources: [], loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState(current => ({ ...current, loading: true, error: null }));
    api
      .apiResources(cluster)
      .then(response => {
        if (!cancelled) {
          setState({
            resources: response.resources ?? [],
            loading: false,
            error: null,
          });
        }
      })
      .catch(error => {
        if (!cancelled) {
          setState({
            resources: [],
            loading: false,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, cluster]);

  return state;
}
